<script lang="ts">
    import { onMount } from 'svelte';
    import route from '../Util/routes';
    import { round , createSettingsSubmitHandler } from '@/Util/utils';
    import ProfileSettingsInput from './ProfileSettingsInput.svelte';

    export let profileParams = null;
    export let showLTP: boolean = false;
    export let activity = null;
    export let readonly = false;
    export let smallSize = false;
    export let title = null;
    export let hideTitle = false;
    export let hideLabels = false;
    export let hideUnits = false;
    export let unitLabels = {pp:"Watts", hie:"KJ", ftp:"Watts", ltp:"Watts"};
    export let hideInputs = false;
    export let refresh = true;

    $: setSignature (profileParams?.signature)
    export let signature = { pp: 0, hie: 0, ftp: 0, ltp: 0 };
    let inputs ={pp:null, hie:null, ftp:null};
    export let auto_calc = false; 
    export function setSignature(val) {
        if(!val) return
        signature.pp = round(val.pp, 0);
        if(val?.atc) signature.hie = round(val.atc / 1000, 1);
        else if (val?.hie) signature.hie = round(val.hie, 1);
        signature.ftp = round(val.ftp, 0);
        signature.ltp = round(val.ltp, 0);
    }
    export function getSignature(){
        return {...signature};
    }

    export function setActivity(value){
        activity = value;
    }

    export function getBody(){
        return {...signature, path:activity?.path, auto_calc, refresh};
    }

    function callbackFunction(val){
        if (val?.signature) {
            setSignature(val.signature)
        }
    }

    export const postFitnessSignature = route('UserController@postFitnessSignature');
    let _submitHandler = createSettingsSubmitHandler(postFitnessSignature, getBody, callbackFunction);
    export async function submitHandler(){
        return _submitHandler();
    }

    export let entry = null;
    export function setEntry(entry){
        if(!entry) return null;
        setSignature(entry?.fs);
        setActivity({path:activity?.path, date:activity?.start_date, name:activity?.name});
    }
    $:setEntry(entry);
    export function getEditableValues(){
        return {... signature };
    }

    export const newEntry = (activity) => {
        return {path:activity?.path, date:activity?.start_date, name:activity?.name, fs:{ pp: 0, hie: 0, ftp: 0, ltp: 0 }, state:"new"}
    }

    export function validate(){
        return _validate();
    }
    
    function _validate(){
        if(auto_calc) return true;
        if(!(signature?.pp && signature?.hie && signature?.ftp)) return false;

        if(signature.pp  < 150){
            setValidation(inputs.pp,'Peak Power must be greater than 150');
            return false;
        }
        if(signature.pp > 2500){
            setValidation(inputs.pp,'Peak Power must be less than 2500');
            return false;
        }
        if( signature.hie < 3){
            setValidation(inputs.hie,'High Intensity Energy must be greater than 3 KJ');
            return false;
        }
        if(signature.hie > 70){
            setValidation(inputs.hie,'High Intensity Energy must be less than 70 KJ');
            return false;
        }
        if(signature.ftp < 50 ){
            setValidation(inputs.ftp,'Functional Threshold Power must be greater than 50');
            return false;
        }
        if(signature.ftp > 500){
            setValidation(inputs.ftp,'Functional Threshold Power must be less than 500');
            return false;
        }
        if(2*signature.ftp > signature.pp){
            setValidation(inputs.ftp,'Functional Threshold Power must be less than half of Peak Power');
            return false;
        }
        return true;
    }


    function setValidation(input, msg='Invalid Entry'){
        input.setCustomValidity(msg);
        input.reportValidity();
    }

    function clearValidation(input){
        input.setCustomValidity('');
        input.reportValidity();
    }

    function inputsOnInput(event){
        clearValidation(event.currentTarget);
    }

    onMount(()=>{
        setSignature(signature);//handles initial rounding/formatting
    })
    
    
</script>
<div class="fs-container flex flex-col">
    {#if !hideTitle}
    <div id="subtitle" class="mb-7">
        <h4>{title ?? "Fitness Signature"}</h4>
    </div>
    {/if} 
    <div class="fitness-signature-form" class:smallSize class:hideLabels class:hideUnits class:hideInputs class:showLTP>
        <!-- <div class="fs-row" class:showLTP >  -->
        <div class="ltp-div form-group" style={showLTP?"":"display:none;"}> 
            <ProfileSettingsInput bind:el={inputs.ftp} label={'Lower Threshold Power'} on:input={inputsOnInput} unitLabel={unitLabels.ftp} bind:value={signature.ltp} min={0} step={0.1} disabled={readonly} {hideInputs} {hideLabels} {hideUnits}>
                <i class='fa fa-bolt' />
            </ProfileSettingsInput>
        </div>
        <div class="ftp-div form-group">
            <ProfileSettingsInput bind:el={inputs.ftp} label={'Threshold Power'} on:input={inputsOnInput} unitLabel={unitLabels.ftp} bind:value={signature.ftp} min={0} step={0.1} color="var(--color-xlss)" disabled={readonly} {hideInputs} {hideLabels} {hideUnits}>
                <i class='fa fa-bolt' />
            </ProfileSettingsInput>
        </div>
        <div class="hie-div form-group">
            <ProfileSettingsInput bind:el={inputs.hie} label={'High Intensity Energy'} on:input={inputsOnInput} unitLabel={unitLabels.hie} bind:value={signature.hie} min={0} step={1} color="var(--color-xhss)" disabled={readonly} {hideInputs} {hideLabels} {hideUnits}>
                <i class="fa fa-line-chart" />
            </ProfileSettingsInput>
        </div>
        <div class="pp-div form-group">
            <ProfileSettingsInput bind:el={inputs.pp} label={'Peak Power'} on:input={inputsOnInput} unitLabel={unitLabels.pp} bind:value={signature.pp} min={0} step={0.1} color="var(--color-xpss)" disabled={readonly} {hideInputs} {hideLabels} {hideUnits}>
                <i class="fa fa-bolt" />
            </ProfileSettingsInput>
        </div>
    </div>
</div>
<style lang="scss">

.form-group{
    flex: 1 1 150px;
    margin: 0;
    position: relative;
}

.fs-container{
    container-name: fs-container;
    container-type: inline-size;
    width: 100%;
    height: 100%;
}
.fitness-signature-form{
    display:grid;
    grid-template-columns: repeat(12, 1fr) ;
    grid-template-rows: repeat(12, 1fr);
    height: 100%;
    align-items: center;
    >div{
        height: 100%;
    }
}

.fitness-signature-form{
    >div{
        margin: 0 4px;
    }
    .ftp-div{
        grid-column: 1 /span 4;
        grid-row: 1 / span 12;
    }
    .hie-div{
        grid-column: 5 /span 4;
        grid-row: 1 / span 12;
    }
    .pp-div{
        grid-column: 9 /span 4;
        grid-row: 1 / span 12;
    }
}

@container fs-container (max-width: 600px) {

    .fitness-signature-form{
        row-gap: 12px;
        .ftp-div{
            grid-row: 9 /span 4 !important;
            grid-column: 1 / span 12 !important;
        }
        .hie-div{
            grid-row: 5 /span 4 !important;
            grid-column: 1 / span 12 !important;
        }
        .pp-div{
            grid-row: 1 /span 4 !important;
            grid-column: 1 / span 12 !important;
        }
    }

    .fitness-signature-form.showLTP{
        .ltp-div{
            grid-row: 10 /span 3 !important;
            grid-column: 1 / span 12 !important;
        }
        .ftp-div{
            grid-row: 7 /span 3 !important;
            grid-column: 1 / span 12 !important;
        }
        .hie-div{
            grid-row: 4 /span 3 !important;
            grid-column: 1 / span 12 !important;
        }
        .pp-div{
            grid-row: 1 /span 3 !important;
            grid-column: 1 / span 12 !important;
        }
    }
}

@container fs-container (min-width: 600px) {
    .fitness-signature-form{
        >div{
            margin: 0 4px;
        }
        .ftp-div{
            grid-column: 1 /span 4;
            grid-row: 1 / span 12;
        }
        .hie-div{
            grid-column: 5 /span 4;
            grid-row: 1 / span 12;
        }
        .pp-div{
            grid-column: 9 /span 4;
            grid-row: 1 / span 12;
        }
    }

    .fitness-signature-form.showLTP{

        .ltp-div{
            grid-column: 10 /span 3;
            grid-row: 1 / span 12;
        }
        .ftp-div{
            grid-column: 1 /span 3;
            grid-row: 1 / span 12;
        }
        .hie-div{
            grid-column: 4 /span 3;
            grid-row: 1 / span 12;
        }
        .pp-div{
            grid-column: 7 /span 3;
            grid-row: 1 / span 12;
        }
    }
}

</style>
